import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { CSSProperties } from 'styled-components';

import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';

import {
  Chip,
  DEFAULT_COLOR,
  ImageContainer,
} from 'components/rewards/AnyRewardConfig';
import { Variant } from 'components/rewards/NewAnyRewardConfig';

import type { RewardMultiplier_lineup } from './__generated__/index.graphql';

type Props = {
  lineup: RewardMultiplier_lineup;
  variant?: Variant;
};

export const RewardMultiplier = ({ lineup, variant = Variant.CHIP }: Props) => {
  const boost = lineup.rewardMultiplier - 1;
  if (boost <= 0) {
    return null;
  }

  if (variant === Variant.TEXT) {
    return (
      <Horizontal gap={0.5}>
        <Box gradient="green" size={1.5} />
        <BrandXS
          as="p"
          bold
          className="text-center text-gradient-color"
          style={
            {
              '--text-gradient-color':
                'linear-gradient( 323deg, rgba(var(--c-rgb-rivals-reputation), 0.8) -3.1%, rgba(var(--c-rgb-green-400), 1) 52.7%)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            id="RewardMultiplier.multiplier"
            defaultMessage="{bonusPct, number, ::sign-always .## percent} boost"
            values={{
              bonusPct: lineup.rewardMultiplier - 1,
            }}
          />
        </BrandXS>
      </Horizontal>
    );
  }

  return (
    <Chip style={{ '--color': DEFAULT_COLOR } as CSSProperties}>
      <ImageContainer>
        <Box gradient="green" />
      </ImageContainer>
      <BrandXS
        as="p"
        bold
        className="text-center text-gradient-color"
        style={
          {
            '--text-gradient-color':
              'linear-gradient( 323deg, rgba(var(--c-rgb-rivals-reputation), 0.8) -3.1%, rgba(var(--c-rgb-green-400), 1) 52.7%)',
          } as CSSProperties
        }
      >
        <FormattedNumber
          value={lineup.rewardMultiplier - 1}
          style="percent"
          maximumFractionDigits={2}
          signDisplay="always"
        />
      </BrandXS>
    </Chip>
  );
};

RewardMultiplier.fragments = {
  lineup: gql`
    fragment RewardMultiplier_lineup on So5Lineup {
      id
      rewardMultiplier
    }
  ` as TypedDocumentNode<RewardMultiplier_lineup>,
};
